import {X, XIcon, Copy} from "lucide-react";
import {cn} from "../ui/lib/utils";
import React, {useEffect, useRef, useState} from "react";
import {Popover, PopoverContent, PopoverTrigger} from "../ui/popover";
import {Command, CommandGroup, CommandItem, CommandList} from "../ui/command";
import {Attribute} from "../../types/telemetry";
import humanFormat from "human-format";
import {useToast, toast} from "../ui/use-toast";
import {v4 as uuidv4} from "uuid";


export function GroupByPill(props: {
    attributeKey: string,
    groupBy: string[],
    setGroupBy: (groupBy: string[]) => void
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(props.attributeKey);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const newValue = editValue.trim();
            if (newValue && newValue !== props.attributeKey) {
                const newGroupBy = props.groupBy.map(val =>
                    val === props.attributeKey ? newValue : val
                );
                props.setGroupBy(newGroupBy);
            }
            setIsEditing(false);
        } else if (e.key === 'Escape') {
            setEditValue(props.attributeKey);
            setIsEditing(false);
        }
    };

    const handleBlur = () => {
        const newValue = editValue.trim();
        if (newValue && newValue !== props.attributeKey) {
            const newGroupBy = props.groupBy.map(val =>
                val === props.attributeKey ? newValue : val
            );
            props.setGroupBy(newGroupBy);
        } else {
            setEditValue(props.attributeKey);
        }
        setIsEditing(false);
    };

    return <div className="group justify-start items-start flex hover:cursor-pointer" onClick={(e) => {
        // Stop propagation for the entire pill component
        e.stopPropagation();
    }}>
        <div
            className="p-2 bg-backgrounddark rounded-tl rounded-bl border justify-start items-start flex">
            {isEditing ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    className="bg-transparent border-none outline-none focus:ring-0 p-0 text-sm text-textmedium font-medium font-['Inter'] leading-[14px]"
                    style={{width: `${Math.max(editValue.length * 8, 50)}px`}}
                />
            ) : (
                <div
                    className="text-center text-textmedium text-sm font-medium font-['Inter'] leading-[14px] cursor-text"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsEditing(true);
                    }}
                >
                    {props.attributeKey}
                </div>
            )}
        </div>
        <div
            onClick={(e) => {
                e.stopPropagation();
                const newGroupBy = props.groupBy.filter((value) => value !== props.attributeKey);
                props.setGroupBy(newGroupBy);
            }}
            className="h-8 pt-[3px] bg-backgroundlight hover:bg-primary hidden group-hover:block group-hover:border-t group-hover:border-b group-hover:border-r group-hover:rounded-r text-textmedium">
            <XIcon/>
        </div>
    </div>;
}

function handleFilterUpdate(props: {
    key?: string;
    attributeKey: string;
    attributeValue: string;
    filter: Map<string, string[]>;
    setFilter: (filter: Map<string, string[]>) => void;
    isExclude?: boolean;
    excludeFilter?: Map<string, string[]>;
    setExcludeFilter?: (filter: Map<string, string[]>) => void;
    isEditable?: boolean;
    regexes?: string[];
    setRegexes?: (regexes: string[]) => void;
    excludeRegexes?: string[];
    setExcludeRegexes?: (regexes: string[]) => void;
    isRegex?: boolean;
    additionalRemoveAction?: () => void;
    dropDownFunction?: (key: string) => Promise<Attribute[]>
}, value: string) {
    if (props.isRegex) {
        let newRegexes = props.isExclude ? [...props.excludeRegexes!] : [...props.regexes!];
        // Replace the old value with the new value
        const index = newRegexes.indexOf(props.attributeValue);
        newRegexes[index] = value;
        if (props.isExclude) {
            props.setExcludeRegexes!(newRegexes);
            return;
        } else {
            props.setRegexes!(newRegexes);
            return;
        }
    }

    const newFilter = props.isExclude ? new Map(props.excludeFilter!) : new Map(props.filter);
    const parsedValue = value.split(" || ");
    newFilter.set(props.attributeKey, parsedValue);
    if (props.isExclude) {
        props.setExcludeFilter!(newFilter);
        return
    } else {
        props.setFilter(newFilter);
        return
    }
}

export function Pill(props: {
    key?: string,
    attributeKey: string,
    attributeValue: string,
    filter: Map<string, string[]>,
    setFilter: (filter: Map<string, string[]>) => void
    isExclude?: boolean
    excludeFilter?: Map<string, string[]>
    setExcludeFilter?: (filter: Map<string, string[]>) => void
    isEditable?: boolean
    regexes?: string[]
    setRegexes?: (regexes: string[]) => void
    excludeRegexes?: string[]
    setExcludeRegexes?: (regexes: string[]) => void
    isRegex?: boolean
    additionalRemoveAction?: () => void
    dropDownFunction?: (key: string) => Promise<Attribute[]>
    dialogRef?: React.RefObject<HTMLElement>
}) {
    const {toast} = useToast();
    const valueRef = useRef(null);
    const [dropDownValues, setDropDownValues] = React.useState<Attribute[]>([]);
    const [xButtonPosition, setXButtonPosition] = React.useState({top: 0, left: 0});
    const pillRef = useRef<HTMLDivElement>(null);
    const [isOpened, setIsOpened] = React.useState(false);
    const [isValueInputFocused, setIsValueInputFocused] = React.useState(false);
    const [isCommandFocused, setCommandFocused] = React.useState(false);
    const [value, setValue] = React.useState("");
    const [valueCursorPosition, setValueCursorPosition] = React.useState(0);

    useEffect(() => {
        if (valueRef?.current && props.attributeValue === "") {
            // @ts-ignore
            valueRef.current.focus();
        }
        // Update button positions when value changes
        updateXButtonPosition();
    }, [valueRef, props.attributeValue, value]);

    const updateXButtonPosition = () => {
        if (!pillRef.current) return;

        if (props.dialogRef && props.dialogRef.current) {
            // If inside a dialog, position relative to dialog
            const pillRect = pillRef.current.getBoundingClientRect();
            const dialogRect = props.dialogRef.current.getBoundingClientRect();

            const height = pillRect.height || 32;
            setXButtonPosition({
                top: (pillRect.top - dialogRect.top + (height - 32) / 2),
                left: pillRect.right - dialogRect.left - 1
            });
        } else {
            // Regular positioning relative to viewport
            const rect = pillRef.current.getBoundingClientRect();
            const height = rect.height || 32; // fallback to 32 if height is 0 on initial render
            setXButtonPosition({
                top: rect.top + (height - 32) / 2,
                left: rect.right
            });
        }
    };

    useEffect(() => {
        // Initial position calculation with a small delay to ensure DOM is ready
        const timer = setTimeout(updateXButtonPosition, 30);

        // Listen for scroll events on all scrollable parents
        const scrollableParents = getScrollableParents(pillRef.current);
        scrollableParents.forEach(parent => {
            parent.addEventListener('scroll', updateXButtonPosition, {passive: true});
        });
        window.addEventListener('resize', updateXButtonPosition);
        window.addEventListener('scroll', updateXButtonPosition, {passive: true});

        // Add interval to recalculate every second
        const interval = setInterval(updateXButtonPosition, 1000);

        return () => {
            clearTimeout(timer);
            clearInterval(interval);
            scrollableParents.forEach(parent => {
                parent.removeEventListener('scroll', updateXButtonPosition);
            });
            window.removeEventListener('resize', updateXButtonPosition);
            window.removeEventListener('scroll', updateXButtonPosition);
        };
    }, []);

    // Helper function to get all scrollable parents
    const getScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
        const scrollableParents: HTMLElement[] = [];
        let currentElement = element?.parentElement;

        while (currentElement) {
            if (currentElement.scrollHeight > currentElement.clientHeight || currentElement.scrollWidth > currentElement.clientWidth) {
                scrollableParents.push(currentElement);
            }
            currentElement = currentElement.parentElement;
        }

        return scrollableParents;
    };

    // Add effect to update position when filter changes
    useEffect(() => {
        // Small delay to ensure DOM has updated
        setTimeout(updateXButtonPosition, 0);
    }, [props.filter, props.excludeFilter, props.regexes, props.excludeRegexes]);

    useEffect(() => {
        if (!isValueInputFocused) {
            return
        }
        if (!props.dropDownFunction) {
            return
        }
        props.dropDownFunction(props.attributeKey).then((values) => {
            setDropDownValues(values);
        })
    }, [isValueInputFocused, props.attributeKey]);

    const copyToClipboard = () => {
        const filterObj = {
            key: props.attributeKey,
            values: props.isRegex ?
                [props.attributeValue] : // For regex, use the attributeValue directly
                (props.isExclude ? props.excludeFilter?.get(props.attributeKey) : props.filter.get(props.attributeKey)),
            isExclude: props.isExclude,
            isRegex: props.isRegex
        };
        navigator.clipboard.writeText(JSON.stringify(filterObj));
        toast({
            description: "Copied filter to clipboard",
            duration: 2000
        });
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain').trim();
        document.execCommand('insertText', false, text);
    };

    return <div ref={pillRef} className="group justify-start items-start flex hover:cursor-pointer max-w-full relative">
        <div
            className={cn("bg-backgrounddark rounded-tl rounded-bl justify-start items-start flex border-t border-l border-b")}
            onClick={() => {
                if (valueRef?.current) {
                    // @ts-ignore
                    valueRef.current.focus();
                }
            }}>
            {
                props.isEditable && !props.isRegex &&
                <div suppressContentEditableWarning contentEditable={true} key={props.key} id={"key_input"}
                     onClick={(e) => {
                         e.stopPropagation();
                     }}

                     onKeyDown={(e) => {
                         // if the return key is pressed, blur the input
                         if (e.key === "Enter") {
                             e.preventDefault();
                             e.currentTarget.blur();
                             return
                         }
                     }}
                     onBlur={(e) => {
                         let key = e.currentTarget.textContent!;
                         key = key.replace(/\n/g, "");
                         const newFilter = props.isExclude ? new Map(props.excludeFilter!) : new Map(props.filter);
                         const prevValue = props.isExclude ? props.excludeFilter!.get(props.attributeKey) : props.filter.get(props.attributeKey);
                         newFilter.delete(props.attributeKey);
                         newFilter.set(key, prevValue || []);
                         if (props.isExclude) {
                             props.setExcludeFilter!(newFilter);
                             return
                         } else {
                             props.setFilter(newFilter);
                             return
                         }
                     }}
                     className={cn("h-[30px] hover:cursor-text p-2 w-max max-w-max flex-none text-center text-textmedium text-sm font-medium font-['Inter'] leading-[14px]",
                                "outline-none caret-textmedium")}>{props.attributeKey}</div>
            }
            {
                (!props.isEditable || props.isRegex) &&
                <div
                    className={cn("p-2 text-center text-textmedium text-sm font-medium leading-[14px]")}>{props.attributeKey}
                </div>
            }
        </div>

        <Popover open={isOpened}>
            <PopoverTrigger>
                <div onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpened(!isOpened)
                }
                }
                     className={cn("p-2 border justify-start items-start flex text-white", props.isExclude ? "bg-redtransparenter border-red-500" : "bg-secondarytransparenter border-secondary")}>
                    <div
                        className={cn("text-center text-sm font-medium text-textlight leading-[14px]")}>{props.isExclude ? "!=" : "="}
                    </div>
                </div>
            </PopoverTrigger>
            <PopoverContent
                onInteractOutside={() => {
                    setIsOpened(false)
                }}
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        setIsOpened(false)
                    }
                }}
                className={"bg-backgroundmedium border rounded text-textmedium"}>
                <Command>
                    <CommandList>
                        <CommandGroup>
                            <CommandItem
                                className={"w-full ariahover:cursor-pointer aria-selected:bg-secondarytransparenter aria-selected:border aria-selected:border-secondary aria-selected:text-textlight hover:bg-primarytransparent hover:text-textlight"}
                                onSelect={() => {
                                    if (!props.isExclude) {
                                        // Do nothing if the filter already exists
                                        return
                                    }

                                    if (props.isRegex) {
                                        const excludes = props.excludeRegexes!.filter((value) => value !== props.attributeValue);
                                        props.setRegexes!(props.regexes!.concat(props.attributeValue));
                                        props.setExcludeRegexes!(excludes);
                                        return
                                    }

                                    // Remove from the exclude filter if it exists and add to the filter
                                    const newFilter = new Map(props.filter);
                                    const excludeFilter = new Map(props.excludeFilter!);
                                    const value = excludeFilter.get(props.attributeKey);
                                    excludeFilter.delete(props.attributeKey);
                                    newFilter.set(props.attributeKey, value!);
                                    props.setExcludeFilter!(excludeFilter);
                                    props.setFilter(newFilter);
                                }}

                            >=</CommandItem>
                            <CommandItem
                                className={"w-full ariahover:cursor-pointer aria-selected:bg-secondarytransparenter aria-selected:border aria-selected:border-secondary aria-selected:text-textlight hover:bg-primarytransparent hover:text-textlight"}
                                onSelect={() => {
                                    if (props.isExclude) {
                                        // Do nothing if the filter already exists
                                        return
                                    }

                                    if (props.isRegex) {
                                        const regexes = props.regexes!.filter((value) => value !== props.attributeValue);
                                        props.setExcludeRegexes!(props.excludeRegexes!.concat(props.attributeValue));
                                        props.setRegexes!(regexes);
                                        return
                                    }

                                    // Remove from the filter if it exists and add to the exclude filter
                                    const newFilter = new Map(props.filter);
                                    const excludeFilter = new Map(props.excludeFilter!);
                                    const value = newFilter.get(props.attributeKey);
                                    newFilter.delete(props.attributeKey);
                                    // If there's already an exclude filter for this key, append the value to the array
                                    const prevExcludeValue = excludeFilter.get(props.attributeKey);
                                    if (prevExcludeValue) {
                                        excludeFilter.set(props.attributeKey, prevExcludeValue.concat(...value!));
                                    } else {
                                        excludeFilter.set(props.attributeKey, value!);
                                    }
                                    props.setFilter(newFilter);
                                    props.setExcludeFilter!(excludeFilter);
                                }}>!=</CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>

        <Popover open={(isValueInputFocused || isCommandFocused) && dropDownValues.length > 0 && dropDownValues.filter(
            (key) => value.length == 0 || key.value.toLowerCase().includes(getCurrentSegmentValue(value, valueCursorPosition).trim())).length > 0}
        >
            <PopoverContent
                onKeyDown={e => {
                    if (e.key !== "ArrowUp" && e.key !== "ArrowDown" && e.key !== "Enter" && e.key !== "Tab") {
                        document.getElementById("value_input_" + props.attributeKey)!.focus();
                    }
                }}
                onOpenAutoFocus={(e) => e.preventDefault()} align={"start"}
                className={"bg-backgroundmedium border rounded text-textmedium mt-[12px]"}>
                <Command
                    id={"inputpillpopovercontent"} onBlur={() => setCommandFocused(false)}>
                    <CommandList>
                        <CommandGroup>
                            {
                                dropDownValues && dropDownValues.filter(
                                    (key) => value.length == 0 || key.value.toLowerCase().includes(getCurrentSegmentValue(value, valueCursorPosition).trim())
                                ).map((key) => {
                                    return <CommandItem
                                        className={cn("w-full ariahover:cursor-pointer justify-between hover:bg-secondarytransparenter hover:border hover:border-secondary hover:text-textlight", isCommandFocused ? "aria-selected:bg-secondarytransparenter aria-selected:border aria-selected:border-secondary aria-selected:text-textlight" : "")}
                                        onSelect={() => {
                                            // Get the current segment
                                            let cursorPosition = valueCursorPosition;
                                            let value = document.getElementById("value_input_" + props.attributeKey)!.textContent!;
                                            let currentSegment = getCurrentSegment(value, cursorPosition);
                                            let segments = value.split("||").map((segment) => segment.trim());
                                            segments[currentSegment] = key.value;
                                            let newValue = segments.join(" || ");
                                            handleFilterUpdate(props, newValue);
                                            // document.getElementById("free_text_input")!.focus();
                                        }}>{key.value}
                                        <div
                                            className={"font-bold text-textdark"}>{key.volume !== 0 ? humanFormat(key.volume, {maxDecimals: 1}) : "-"}</div>
                                    </CommandItem>
                                })
                            }
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
            <PopoverTrigger asChild={true}>
                <div
                    className={cn("w-max border-t border-b border-r rounded-r group-hover:border-t group-hover:border-b group-hover:border-r-0 group-hover:rounded-r-none justify-center items-center flex max-w-[500px]", " bg-backgroundmedium")}>
                    {
                        props.isEditable &&
                        <div
                            onClick={(e) => {
                                setValueCursorPosition(getCursorPosition(document.getElementById("value_input_" + props.attributeKey)));
                                e.stopPropagation();
                            }}
                            onFocus={(e) => {
                                setValueCursorPosition(getCursorPosition(document.getElementById("value_input_" + props.attributeKey)));
                                setIsValueInputFocused(true);
                            }}
                            onBlur={(e) => {
                                setIsValueInputFocused(false);
                                const text = e.currentTarget.textContent || "";
                                handleFilterUpdate(props, text.trim());
                            }}
                            onPaste={handlePaste}
                            onSelect={(e) => {
                                setValueCursorPosition(getCursorPosition(document.getElementById("value_input_" + props.attributeKey)));
                            }}
                            suppressContentEditableWarning
                            contentEditable={true} key={props.key}
                            onKeyUp={(e) => {
                                setValue(e.currentTarget.textContent!);
                            }}
                            onKeyDown={(e) => {
                                setValueCursorPosition(getCursorPosition(document.getElementById("value_input_" + props.attributeKey)));
                                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                    setCommandFocused(true);
                                    document.getElementById("inputpillpopovercontent")!.focus();
                                }
                                // if the return or tab key is pressed, blur the input
                                if (e.key === "Enter" || e.key === "Tab") {
                                    e.preventDefault();
                                    handleFilterUpdate(props, value.replace(/\n/g, ""));
                                    e.currentTarget.blur();
                                    setIsValueInputFocused(false);
                                    return
                                }
                            }}
                            ref={valueRef}
                            id={"value_input_" + props.attributeKey}
                            className={cn("h-[30px] hover:cursor-text p-2 w-max flex-none text-center text-textmedium text-sm font-medium text-nowrap overflow-x-auto overflow-y-hidden no-scrollbar max-w-full",
                                       "outline-none caret-textmedium leading-[14px]")}>{props.attributeValue}</div>
                    }
                    {
                        !props.isEditable &&
                        <div
                            id={"value_input_" + props.attributeKey}
                            className={cn("p-2 text-left text-textlight text-sm font-medium text-nowrap overflow-x-auto overflow-y-hidden no-scrollbar")}>{props.attributeValue}
                        </div>
                    }
                </div>
            </PopoverTrigger>
        </Popover>
        <div
            onClick={copyToClipboard}
            style={{top: xButtonPosition.top, left: xButtonPosition.left - 1}}
            className="fixed h-8 pt-[8px] bg-backgroundmedium hover:bg-primary hidden group-hover:block border-t border-b text-textlight z-50">
            <Copy className="w-4 h-4"/>
        </div>
        <div
            onClick={() => {
                if (props.isRegex) {
                    const newRegexes = props.isExclude ? props.excludeRegexes!.filter((value) => value !== props.attributeValue) : props.regexes!.filter((value) => value !== props.attributeValue);
                    if (props.isExclude) {
                        props.setExcludeRegexes!(newRegexes);
                    } else {
                        props.setRegexes!(newRegexes);
                    }
                    return
                }

                if (props.isExclude) {
                    const newFilter = new Map(props.excludeFilter!);
                    newFilter.delete(props.attributeKey);
                    props.setExcludeFilter!(newFilter);
                    return
                } else {
                    const newFilter = new Map(props.filter);
                    newFilter.delete(props.attributeKey);
                    props.setFilter(newFilter);
                }
                if (props.additionalRemoveAction) {
                    props.additionalRemoveAction();
                }
            }}
            style={{top: xButtonPosition.top, left: xButtonPosition.left + 15}}
            className="fixed h-8 pt-[8px] bg-backgroundmedium hover:bg-primary hidden group-hover:block border-t border-b border-r rounded-r text-textlight z-50">
            <XIcon className="w-4 h-4"/>
        </div>
    </div>;
}

function getCurrentSegmentValue(s: string, cursorPosition: number) {
    const segments = s.split("||");
    let pos = 0;
    for (let i = 0; i < segments.length; i++) {
        if (pos + segments[i].length >= cursorPosition) {
            return segments[i];
        }
        pos += segments[i].length;
    }
    return segments[segments.length - 1];
}

function getCurrentSegment(s: string, cursorPosition: number) {
    const segments = s.split("||").map((segment) => segment.trim());
    let pos = 0;
    for (let i = 0; i < segments.length; i++) {
        if (pos + segments[i].length >= cursorPosition) {
            return i;
        }
        pos += segments[i].length;
    }
    return segments.length - 1;
}

function getSegmentsStartPositions(s: string) {
    const segments = s.split("||");
    let positions = [];
    let pos = 0;
    for (let i = 0; i < segments.length; i++) {
        positions.push(pos);
        pos += segments[i].length;
    }
    return positions;
}

function getCursorPosition(element: any) {
    var caretOffset = 0;

    if (window.getSelection) {
        if (window.getSelection()!.rangeCount == 0) {
            return 0;
        }
        var range = window.getSelection()!.getRangeAt(0);
        var preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        caretOffset = preCaretRange.toString().length;
    }
    return caretOffset;
}

function handleFilterCreation(s: string, props: {
    placeholderText: string;
    filter: Map<string, string[]>;
    setFilter: (filter: Map<string, string[]>) => void;
    excludeFilter?: Map<string, string[]>;
    setExcludeFilter?: (filter: Map<string, string[]>) => void;
    regexes?: string[];
    setRegexes?: (regexes: string[]) => void;
    inputKeys?: string[];
    disallowKeys?: string[];
}) {
    // If there is a ! in the last character, create an exclude filter
    let isExclude = false;
    if (s.charAt(s.length - 2) === "!") {
        isExclude = true;
        s = s.slice(0, -2);
    } else {
        s = s.slice(0, -1);
    }

    // Check if the key is in the disallowKeys array and show an error message if it is
    if (props.disallowKeys && props.disallowKeys.some(key => s.toLowerCase() === key.toLowerCase())) {
        toast({
            title: "Invalid Filter Key",
            description: "Please use the Environment selector in the second step.",
            variant: "destructive"
        });
        return;
    }
    if (isExclude) {
        const newFilter = new Map(props.excludeFilter!);
        let prevValue = newFilter.get(s);
        if (prevValue) {
            newFilter.set(s, prevValue.concat(""));
        } else {
            newFilter.set(s, [""]);
        }
        props.setExcludeFilter!(newFilter);
        if (prevValue) {
            setTimeout(() => {
                document.getElementById("value_input_" + s)!.focus();
                // select all the content in the element
                document.execCommand('selectAll', false, undefined);
// collapse selection to the end
                document.getSelection()!.collapseToEnd();
            }, 100);
        }
    } else {
        const newFilter = new Map(props.filter);
        let prevValue = newFilter.get(s);
        if (prevValue) {
            newFilter.set(s, prevValue.concat(""));
        } else {
            newFilter.set(s, [""]);
        }
        props.setFilter(newFilter);
        if (prevValue) {
            setTimeout(() => {
                document.getElementById("value_input_" + s)!.focus();
                document.execCommand('selectAll', false, undefined);
// collapse selection to the end
                document.getSelection()!.collapseToEnd();
            }, 100);
        }
    }
}

export function InputPill(props: {
    placeholderText: string,
    filter: Map<string, string[]>,
    setFilter: (filter: Map<string, string[]>) => void,
    excludeFilter?: Map<string, string[]>,
    setExcludeFilter?: (filter: Map<string, string[]>) => void
    regexes?: string[],
    setRegexes?: (regexes: string[]) => void,
    excludeRegexes?: string[],
    setExcludeRegexes?: (regexes: string[]) => void,
    inputKeys?: string[],
    disallowKeys?: string[]
}) {
    const id = uuidv4()
    const [isFocused, setIsFocused] = React.useState(false);
    const [commandFocused, setCommandFocused] = React.useState(false);
    const [value, setValue] = React.useState("");


    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain').trim();

        try {
            const filterObj = JSON.parse(text);
            if (filterObj.key && Array.isArray(filterObj.values)) {
                if (filterObj.isRegex) {
                    if (filterObj.isExclude) {
                        if (props.excludeRegexes && props.setExcludeRegexes) {
                            props.setExcludeRegexes(props.excludeRegexes.concat(filterObj.values));
                        }
                    } else if (props.regexes && props.setRegexes) {
                        props.setRegexes(props.regexes.concat(filterObj.values));
                    }
                } else {
                    if (filterObj.isExclude) {
                        if (props.excludeFilter && props.setExcludeFilter) {
                            const newFilter = new Map(props.excludeFilter);
                            newFilter.set(filterObj.key, filterObj.values);
                            props.setExcludeFilter(newFilter);
                        }
                    } else {
                        const newFilter = new Map(props.filter);
                        newFilter.set(filterObj.key, filterObj.values);
                        props.setFilter(newFilter);
                    }
                }
            }
        } catch (e) {
            // If not valid JSON, treat as regex
            if (props.regexes && props.setRegexes) {
                const newRegexes = props.regexes.concat(text);
                props.setRegexes(newRegexes);
            }
        }
    };

    return <div
        className={cn("grow shrink justify-start items-start flex hover:cursor-pointer")}>
        <div
            className={cn("h-8 border-t bg-backgroundlight border-b border-r rounded-r group-hover:border-r-0 group-hover:rounded-r-none self-stretch justify-center items-center flex border-none bg-backgoundmedium w-full")}>
            <div className={cn("w-full")}>
                <Popover
                    open={(isFocused || commandFocused) && props.inputKeys && props.inputKeys.length > 0 && props.inputKeys.filter((key) => key.toLowerCase().includes(value)).length !== 0}>
                    <PopoverContent
                        onKeyDown={e => {
                            if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
                                document.getElementById("free_text_input" + id)!.focus();
                            }
                        }}
                        onOpenAutoFocus={(e) => e.preventDefault()} align={"start"}
                        className={"bg-backgroundmedium border rounded text-textmedium mt-[12px]"}>
                        <Command
                            id={"inputpillpopovercontent"} onBlur={() => setCommandFocused(false)}>
                            <CommandList>
                                <CommandGroup>
                                    {
                                        props.inputKeys && props.inputKeys!.filter(
                                            (key) => key.toLowerCase().includes(value)
                                        ).map((key) => {
                                            return <CommandItem
                                                className={cn("w-full ariahover:cursor-pointer hover:bg-secondarytransparenter hover:border hover:border-secondary hover:text-textlight ", commandFocused ? "aria-selected:bg-secondarytransparenter aria-selected:border aria-selected:border-secondary aria-selected:text-textlight" : "")}
                                                onSelect={() => {
                                                    document.getElementById("free_text_input" + id)!.textContent = "";
                                                    setValue("");
                                                    handleFilterCreation(key + "=", props);
                                                }}>{key}</CommandItem>
                                        })
                                    }
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                    <PopoverTrigger asChild={true}>
                        <div
                            suppressContentEditableWarning
                            contentEditable={true}
                            id={"free_text_input" + id}
                            onFocus={(e) => {
                                setIsFocused(true);
                            }}
                            onPaste={handlePaste}
                            onKeyUp={(e) => {
                                setValue(e.currentTarget.textContent!);
                                // If up or down arrow is pressed, focus on the command
                                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                    setCommandFocused(true);
                                    document.getElementById("inputpillpopovercontent")!.focus();
                                }
                                if (e.key === "Enter" || e.key === "Tab") {
                                    e.preventDefault();
                                    if (e.currentTarget.textContent === "") {
                                        return
                                    }
                                    let search = e.currentTarget.textContent!;
                                    search = search.replace(/\n/g, "");
                                    // Add a regex
                                    const newRegexes = props.regexes!.concat(search);
                                    props.setRegexes!(newRegexes);
                                    e.currentTarget.textContent = "";
                                    setValue("");
                                    return
                                }

                                if (e.key === "Escape") {
                                    setIsFocused(false);
                                }

                                // If the = key is pressed, create a filter instead of typing the character
                                if (e.key !== "=") {
                                    return
                                }
                                let search = e.currentTarget.textContent!;
                                setValue("");
                                document.getElementById("free_text_input" + id)!.textContent = "";
                                handleFilterCreation(search, props);
                            }}
                            onBlur={(e) => {
                                setIsFocused(false);
                            }}
                            className={cn("h-full flex hover:cursor-text p-2 grow shrink flex-none text-textmedium text-sm font-medium outline-none")}
                            ></div>
                    </PopoverTrigger>
                </Popover>
            </div>
        </div>
    </div>;
}
